/**
 * POC animated lobby — boot flag.
 *
 * Read once at module evaluation, the same way the QA panel reads `?qa=1`: a URL param first
 * (`?fx=1` on, `?fx=0` forces off), then a localStorage fallback (`lobby-fx=1`) for the case where
 * the lobby runs inside the game client iframe and the URL cannot be edited by hand (the QA panel
 * writes it). With the flag off nothing in `shared/effects` mounts, no `data-fx` / `data-motion-tier`
 * attribute is written and the canvas chunk is never requested.
 */

const URL_PARAM = 'fx';
const STORAGE_KEY = 'lobby-fx';

const readFlag = (): boolean => {
  if (typeof window === 'undefined') return false;

  try {
    const value = new URLSearchParams(window.location.search).get(URL_PARAM);
    if (value === '1') return true;
    if (value === '0') return false;
  } catch {
    // URLSearchParams unavailable or invalid query — fall through to storage.
  }

  try {
    return window.localStorage?.getItem(STORAGE_KEY) === '1';
  } catch {
    // localStorage can throw in private mode / sandboxed iframes.
    return false;
  }
};

/** Frozen for the lifetime of the page; a change needs a reload. */
export const FX_ENABLED: boolean = readFlag();

/** Persists the flag for the next boot (QA panel). Returns false when storage is unavailable. */
export const persistFxEnabled = (enabled: boolean): boolean => {
  try {
    if (enabled) {
      window.localStorage.setItem(STORAGE_KEY, '1');
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
    return true;
  } catch {
    return false;
  }
};
